import { omit } from 'lodash'
import * as Yup from 'yup'
import { COGNITO_MESSAGE_ACTIONS } from '../../../../util/cognito.util'
import { SSO_LOGIN_OPTIONS } from '../../../../util/sso.util'

export const PERMISSION_MAPPINGS = [
  {
    moduleName: 'discover',
    title: 'USERPERMISSION.discover',
    subtitle: 'USERPERMISSION.discoversubtitle',
    groupTitle: 'USERPERMISSION.GROUPdiscover',
  },
  {
    moduleName: 'engage',
    title: 'USERPERMISSION.engage',
    subtitle: 'USERPERMISSION.engagesubtitle',
  },
  {
    moduleName: 'secure',
    title: 'USERPERMISSION.secure',
    subtitle: 'USERPERMISSION.securesubtitle',
  },
  {
    moduleName: 'intune',
    title: 'USERPERMISSION.intune',
  },
  {
    moduleName: 'purview',
    title: 'USERPERMISSION.purview',
  },
  {
    moduleName: 'teams',
    title: 'USERPERMISSION.teams',
  },
  {
    moduleName: 'sharepoint',
    title: 'USERPERMISSION.sharepoint',
  },
  {
    moduleName: 'companies',
    title: 'USERPERMISSION.companies',
    groupTitle: 'USERPERMISSION.GROUPcompanies',
  },
  {
    moduleName: 'integrations',
    title: 'USERPERMISSION.integrations',
  },
  {
    moduleName: 'billing',
    title: 'USERPERMISSION.billing',
  },
  {
    moduleName: 'systemUsers',
    title: 'USERPERMISSION.systemUsers',
  },
  {
    moduleName: 'licenseCenter',
    title: 'USERPERMISSION.licenseCenter',
    subtitle: 'USERPERMISSION.licenseCentersubtitle',
  },
  {
    moduleName: 'lookAndFeel',
    title: 'USERPERMISSION.lookAndFeel',
  },
]

const DEFAULT_LOGIN_OPTIONS = [SSO_LOGIN_OPTIONS.AUGMENTT_PASSWORD, SSO_LOGIN_OPTIONS.MICROSOFT_SSO]

// Microsoft 365's limits for the same properties, which a user with no linked Microsoft 365 user enters by hand
const JOB_TITLE_MAX_LENGTH = 128
const DEPARTMENT_MAX_LENGTH = 64
const PHONE_MAX_LENGTH = 64
const OFFICE_LOCATION_MAX_LENGTH = 128

/** A user without saved login options can sign in with every option. */
export const getUserLoginOptions = (user) => user?.login_options?.split(':') ?? DEFAULT_LOGIN_OPTIONS

export const COMPANY_SELECTION_TYPES = {
  COMPANY: 'COMPANY',
  COLLECTION: 'COLLECTION',
}

export const buildInitialValues = ({
  editMode,
  isSocAdminUser,
  currentUserCollectionId,
  user,
  userInfo,
  permissionEntities,
  allCustomersIds,
  allNonNfrCustomersIds,
}) => {
  if (!editMode) {
    const permissionInitialState = {}
    permissionEntities.forEach((permission) => {
      permissionInitialState[permission.id] = permission.default_permission
    })

    return {
      firstName: '',
      lastName: '',
      email: '',
      companySelectionType: !isSocAdminUser ? COMPANY_SELECTION_TYPES.COMPANY : null,
      customers: [],
      isAllCompanies: false,
      excludeNFRCompany: false,
      collectionId: null,
      permissions: permissionInitialState,
      loginOptions: DEFAULT_LOGIN_OPTIONS,
    }
  }

  let isAllCompanies = userInfo?.customers?.length === 1 && userInfo?.customers?.[0] === 0
  const excludeNFRCompany = userInfo?.permissions?.excludeNFRCompany ?? false
  let companySelectionType = user.collectionId ? COMPANY_SELECTION_TYPES.COLLECTION : COMPANY_SELECTION_TYPES.COMPANY
  let collectionId = user.collectionId ?? null
  let customers = []

  if (!isSocAdminUser && currentUserCollectionId && user.collectionId) {
    // An MSP admin in a collection sees its companies as all companies, and saving keeps that collection.
    // Anyone else keeps the user's collection, so saving never widens it to every company.
    collectionId = null
    companySelectionType = COMPANY_SELECTION_TYPES.COMPANY
    isAllCompanies = true
    customers = excludeNFRCompany ? allNonNfrCustomersIds : allCustomersIds
  } else if (isAllCompanies) {
    customers = excludeNFRCompany ? allNonNfrCustomersIds : allCustomersIds
  } else {
    customers = userInfo?.customers?.filter((customer) => customer !== 0) ?? []
  }

  return {
    firstName: user?.first_name ?? '',
    lastName: user.last_name ?? '',
    email: user.email ?? '',
    companySelectionType,
    customers,
    isAllCompanies,
    excludeNFRCompany,
    collectionId,
    permissions: omit(userInfo?.permissions || {}, ['excludeNFRCompany']),
    loginOptions: getUserLoginOptions(user),
    jobTitle: user.job_title ?? '',
    department: user.department ?? '',
    phone: user.phone ?? '',
    officeLocation: user.office_location ?? '',
  }
}

export const getValidationSchema = (intl, isImport = false, includeJobDetails = false) => {
  const requiredName = Yup.string().required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
  const tooLong = intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })
  const name = isImport
    ? requiredName.trim()
    : requiredName.min(2, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_SHORT' })).max(50, tooLong)
  const jobDetails = includeJobDetails && {
    jobTitle: Yup.string().max(JOB_TITLE_MAX_LENGTH, tooLong),
    department: Yup.string().max(DEPARTMENT_MAX_LENGTH, tooLong),
    phone: Yup.string().max(PHONE_MAX_LENGTH, tooLong),
    officeLocation: Yup.string().max(OFFICE_LOCATION_MAX_LENGTH, tooLong),
  }
  return Yup.object({
    firstName: name,
    lastName: name,
    email: Yup.string()
      .email(
        intl.formatMessage({
          id: 'AUTH.VALIDATION.INVALID_EMAIL',
        }),
      )
      .required(
        intl.formatMessage({
          id: 'AUTH.VALIDATION.REQUIRED_FIELD',
        }),
      ),
    loginOptions: Yup.array()
      .min(1)
      .required(
        intl.formatMessage({
          id: 'AUTH.VALIDATION.REQUIRED_FIELD',
        }),
      ),
    ...jobDetails,
  })
}

const hasAugmenttPassword = (loginOptions) =>
  loginOptions?.some((item) => item === SSO_LOGIN_OPTIONS.AUGMENTT_PASSWORD) ?? false

/**
 * Decides whether Cognito sends the invite email: an edit only resends it when the Augmentt password
 * login is newly added, and a new user only gets it when that login is selected.
 */
export const getMessageAction = ({ editMode, initialLoginOptions, loginOptions }) => {
  if (editMode) {
    return !hasAugmenttPassword(initialLoginOptions) && hasAugmenttPassword(loginOptions)
      ? COGNITO_MESSAGE_ACTIONS.RESEND
      : COGNITO_MESSAGE_ACTIONS.SUPPRESS
  }
  return hasAugmenttPassword(loginOptions) ? null : COGNITO_MESSAGE_ACTIONS.SUPPRESS
}

const normalizeName = (name) => (name ?? '').replace(/\s+/g, ' ').trim()
const toOptionalText = (value) => value?.trim() || null

/**
 * Builds the system user request body shared by adding and editing a system user.
 */
export const buildSystemUserPayload = (
  values,
  { hasOrganizationWideAccess, currentUserCollectionId, messageAction, includeHandEnteredDetails = false },
) => {
  let { customers, collectionId } = values
  if (hasOrganizationWideAccess && values.isAllCompanies) {
    // Organization admins send [0], which includes any company created later.
    customers = [0]
  } else if (currentUserCollectionId && values.isAllCompanies) {
    // An MSP admin in a collection only reaches that collection's companies, not every company.
    collectionId = currentUserCollectionId
    customers = []
  }

  return {
    first_name: normalizeName(values.firstName),
    last_name: normalizeName(values.lastName),
    email: values.email,
    customers,
    collectionId,
    permissions: values.permissions,
    login_options: values.loginOptions?.join(':') || null,
    message_action: messageAction,
    exclude_nfr_company: values.excludeNFRCompany,
    ...(includeHandEnteredDetails && {
      job_title: toOptionalText(values.jobTitle),
      department: toOptionalText(values.department),
      phone: toOptionalText(values.phone),
      office_location: toOptionalText(values.officeLocation),
    }),
  }
}

export const getLoginOptions = (intl) => [
  {
    value: SSO_LOGIN_OPTIONS.MICROSOFT_SSO,
    label: intl.formatMessage({ id: 'ADMINISTRATION.LOGIN_OPTIONS.MICROSOFT_SSO' }),
  },
  {
    value: SSO_LOGIN_OPTIONS.AUGMENTT_PASSWORD,
    label: intl.formatMessage({ id: 'ADMINISTRATION.LOGIN_OPTIONS.AUGMENTT_PASSWORD' }),
  },
]

export const getCompanySelectionTypeOptions = (intl) => [
  {
    value: COMPANY_SELECTION_TYPES.COMPANY,
    label: intl.formatMessage({ id: 'USERS.SELECT_COMPANIES' }),
  },
  {
    value: COMPANY_SELECTION_TYPES.COLLECTION,
    label: intl.formatMessage({ id: 'USERS.SELECT_COLLECTION' }),
  },
]
